/// <amd-module name="TFS/Admin/Views/SettingsSearch/SettingsSearch" />
import React = require("react");
import AdminResources = require("TFS/Admin/Views/SettingsSearch/Resources");

import { AdminFeatureTelemetryLogger, AdminTelemetry } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { CardContent, CustomCard } from "VSSUI/Card";
import { Components, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { CustomHeader, HeaderTitle, HeaderTitleArea, TitleSize } from "VSSUI/Header";
import { Dropdown, renderHighlightedText } from "VSSUI/Dropdown";
import { IListItemDetails, ListItem, ScrollableList } from "VSSUI/List";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import {
    ISettingsSearchComponentState,
    OperationState,
    SearchResultSet,
    SearchScopeSelection,
    SettingsSearchConstants,
    SettingsSearchRoutes
} from "TFS/Admin/Views/SettingsSearch/Contracts/DataModels";
import { Icon, IconSize } from "VSSUI/Icon";
import { Pill, PillSize } from "VSSUI/Pill";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";

import { ArrayItemProvider, getItemsValue } from "VSSUI/Utilities/Provider";
import { Button } from "VSSUI/Button";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { Header } from "VSSUI/Header";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { IListBoxItem } from "VSSUI/ListBox";
import { ISettingsSearchComponentProps } from "TFS/Admin/Views/SettingsSearch/admin-settings-search.Props";
import { ISurveyPanelProps } from "TFS/Admin/Views/SurveyPanel/admin-survey-panel.Props";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { KeyCode } from "VSSUI/Util";
import { Observer, UncheckedObserver } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { PillGroup } from "VSSUI/PillGroup";
import { SearchScope } from "TFS/Admin/Common/Contracts/Search.Shared";
import { SettingsSearchSource } from "TFS/Admin/Views/SettingsSearch/Sources/SettingsSearchSource";
import { Tooltip } from "VSSUI/TooltipEx";
import { ZeroData } from "VSSUI/ZeroData";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { getAssetLocation } from "VSS/Platform/Location";
import { routeUrl } from "VSS/Platform/Location";

export class SettingsSearchComponent extends VssComponent<ISettingsSearchComponentProps, ISettingsSearchComponentState> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);
    private performanceRecorded = false;
    private surveyRecorded: ObservableValue<boolean>;

    private sourceProvider: SettingsSearchSource;
    private _searchResults: { [key: string]: SearchResultSet };
    private projectSelection: SearchScopeSelection = { selection: new DropdownSelection() };
    private projectListProvider: IObservableArray<IListBoxItem>;
    private operationState: ObservableValue<OperationState>;
    private showFeedbackPanel: ObservableValue<boolean>;
    private organizationName: string;
    private messagesService: IGlobalMessagesService;
    protected itemProvider: IObservableValue<ArrayItemProvider<SearchResultSet>>;

    public constructor(props: ISettingsSearchComponentProps, context?: any) {
        super(props, context);
        this.state = {};
        this.projectListProvider = new ObservableArray<IListBoxItem>([]);
        this._searchResults = {};
        this.itemProvider = new ObservableValue<ArrayItemProvider<SearchResultSet>>(new ArrayItemProvider<SearchResultSet>([]));
        this.operationState = new ObservableValue<OperationState>(OperationState.Loading);
        this.sourceProvider = new SettingsSearchSource(props.pageContext);
        this.showFeedbackPanel = new ObservableValue<boolean>(false);
        this.surveyRecorded = new ObservableValue<boolean>(false);

        this.messagesService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        this.organizationName = vssPageData ? vssPageData.hostName : "";
    }

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.SettingsSearch, Feature.SettingsSearch, action, properties);
    };

    private _getProjectsAndResult = () => {
        var actionTelemetryLogger = this._startActionCapture("Fetch Project List", {});
        this.sourceProvider
            .getProjects()
            .then(
                newProjects => {
                    var currentList: IListBoxItem[] = [];
                    newProjects.forEach(project => {
                        const entry: IListBoxItem = {
                            id: project.id,
                            text: project.name
                        };
                        currentList.push(entry);
                    });
                    this.projectListProvider.value = currentList.sort((item1, item2) => item1.text!.localeCompare(item2.text!, navigator.language));
                    if (this.projectListProvider.value.length > 0) {
                        this.projectSelection.value = this.projectListProvider.value[0];
                        this.projectSelection.selection.select(0);
                    }
                    this._getSearchResults(this.props.searchTerm);
                },
                reject => {
                    actionTelemetryLogger.captureError(reject.message || String(reject));
                    this._getSearchResults(this.props.searchTerm);
                }
            )
            .catch(reject => {
                actionTelemetryLogger.captureError(SettingsSearchConstants.GetProjectsListError);
                this._getSearchResults(this.props.searchTerm);
            });
    };

    public componentDidMount() {
        const actionTelemetryLogger = this._startActionCapture("Component Did Mount", {});
        this._getProjectsAndResult();
    }

    public getSnapshotBeforeUpdate() {
        this.operationState.value = OperationState.Loading;
        this.projectListProvider.value.length == 0 ? this._getProjectsAndResult() : this._getSearchResults(this.props.searchTerm);
        return null;
    }

    private _getSearchResults(searchKey: string) {
        const actionTelemetryLogger = this._startActionCapture("Get Search Results", {});
        this.sourceProvider.getSettingSearchResults(searchKey).then(
            results => {
                this._searchResults = {};
                results.results.forEach(result => {
                    const key = result.scope.toString();
                    if (this._searchResults[key] == undefined) {
                        this._searchResults[key] = {
                            setTitle: this.sourceProvider.getSetTitle(result.scope),
                            results: [result],
                            seachScope: result.scope
                        };
                    } else {
                        this._searchResults[key].results.push(result);
                    }
                });
                // Remove project scope results if no projects were found
                const scopes = Object.keys(this._searchResults);
                const projectScopeIndex = scopes.indexOf(SearchScope.Project.toString());
                if (this.projectListProvider.value.length === 0 && projectScopeIndex > -1) {
                    scopes.splice(projectScopeIndex, 1);
                }

                actionTelemetryLogger.captureTelemetry({ searchResultCount: results.count });
                this.itemProvider.value = new ArrayItemProvider(scopes.map(s => this._searchResults[s]));
                this.operationState.value = OperationState.Normal;
                announce(format(AdminResources.SearchResultsCount, this._getResultCount(this.itemProvider.value)));
            },
            reject => {
                actionTelemetryLogger.captureError("Search Operation Error");
                this.operationState.value = OperationState.Error;
                this.itemProvider.value = new ArrayItemProvider([]);
                announce(format(AdminResources.SearchResultsCount, "0"));
            }
        );
    }

    public render(): JSX.Element {
        if (this.projectListProvider.value.length > 0 && !this.performanceRecorded) {
            const performanceService: IVssPerformanceService = this.props.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.markTimeToInteractive(Area.Admin, Feature.SettingsSearch);
            this.performanceRecorded = true;
        }
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow flex-column settings-page">
                    <CustomHeader className="bolt-header-with-commandbar search-result-header">
                        <HeaderTitleArea className="flex-column">
                            <HeaderTitle className="text-ellipsis" titleSize={TitleSize.Large}>
                                {AdminResources.SettingsSearchTitle}
                            </HeaderTitle>
                            <UncheckedObserver itemProvider={this.itemProvider}>
                                {(observableProps: { itemProvider: IObservableValue<ArrayItemProvider<SearchResultSet>> }) =>
                                    this._searchResultsCount(observableProps.itemProvider)
                                }
                            </UncheckedObserver>
                        </HeaderTitleArea>
                    </CustomHeader>
                    <Observer showFeedbackPanel={this.showFeedbackPanel}>
                        {(observableProps: { showFeedbackPanel: boolean }) =>
                            observableProps.showFeedbackPanel && (
                                <WrappedComponent<ISurveyPanelProps>
                                    dependencies={["ms.vss-admin-web.admin-survey-panel-component"]}
                                    wrappedType={"ms.vss-admin-web.admin-survey-panel"}
                                    wrappedProps={{
                                        pageContext: this.props.pageContext,
                                        feature: SettingsSearchConstants.SettingsSeachFeatureFlag,
                                        telemetryLogger: this.telemetryLogger,
                                        onCancel: () => (this.showFeedbackPanel.value = false),
                                        onCompleted: () => {
                                            this._onQueueToast(AdminResources.SurveySubmitted);
                                            this.surveyRecorded.value = true;
                                        }
                                    }}
                                />
                            )
                        }
                    </Observer>

                    <Observer operationState={this.operationState} surveyRecorded={this.surveyRecorded}>
                        {(observableProps: { operationState: OperationState; surveyRecorded: boolean }) => (
                            <>
                                {observableProps.operationState == OperationState.Loading && (
                                    <div className="flex-grow flex-column justify-center">
                                        <Spinner size={SpinnerSize.large} />
                                    </div>
                                )}
                                {observableProps.operationState == OperationState.Error && this._zeroData()}
                                {observableProps.operationState == OperationState.Normal && (
                                    <UncheckedObserver itemProvider={this.itemProvider}>
                                        {(observableProps: { itemProvider: IObservableValue<ArrayItemProvider<SearchResultSet>> }) =>
                                            observableProps.itemProvider.value.length != 0 ? (
                                                <div className="flex-grow flex-column">
                                                    <ScrollableList itemProvider={this.itemProvider.value} renderRow={this.renderRow} width="100%" />
                                                </div>
                                            ) : (
                                                this._zeroData()
                                            )
                                        }
                                    </UncheckedObserver>
                                )}
                                {observableProps.operationState != OperationState.Loading && this._survey(observableProps.surveyRecorded)}
                            </>
                        )}
                    </Observer>
                </Page>
            </Surface>
        );
    }

    private onSurveyCardClicked(positiveFeedback: boolean) {
        if (!this.surveyRecorded.value) {
            if (positiveFeedback) {
                this.surveyRecorded.value = true;
                this._onQueueToast(AdminResources.SurveySubmitted);
            }
            const actionTelemetryLogger = this._startActionCapture("Survey Clicked", { positiveFeedback: positiveFeedback });
        }
        if (!positiveFeedback) {
            this.showFeedbackPanel.value = true;
        }
    }

    private _zeroData(): JSX.Element {
        const contextString = this.operationState.value == OperationState.Error ? AdminResources.ServerError : AdminResources.ZeroResultError;
        return (
            <div className="flex-grow flex-column">
                <ZeroData
                    primaryText={contextString}
                    imagePath={getAssetLocation(this.context.pageContext, SettingsSearchConstants.ZeroDataImage)}
                    imageAltText={contextString}
                    secondaryText={AdminResources.TryAgain}
                />
            </div>
        );
    }

    private _searchResultsCount(searchResults: IObservableValue<ArrayItemProvider<SearchResultSet>>) {
        var searchResultsCount = this._getResultCount(searchResults.value);

        return (
            <PillGroup className="margin-top-4">
                <span className="text-ellipsis">{AdminResources.ResultsTotal}</span>
                <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact}>
                    {searchResultsCount}
                </Pill>
            </PillGroup>
        );
    }

    private _getResultCount(searchResults: ArrayItemProvider<SearchResultSet>) {
        var searchResultsCount = 0;
        if (searchResults && searchResults.length > 0) {
            getItemsValue(searchResults).forEach(scope => {
                searchResultsCount += scope.results.length;
            });
        }
        return searchResultsCount;
    }

    private _survey(surveyRecorded: boolean): JSX.Element {
        return (
            <div className="flex-column search-row">
                <div className="search-card-header">
                    <span className="title-xs s">{AdminResources.SurveySubtitle}</span>
                </div>
                {surveyRecorded ? (
                    <div className="bolt-focus-treatment survey-div feedback-message">
                        <span>{AdminResources.SurveyThankYouMessage}</span>
                    </div>
                ) : (
                    <div className="bolt-focus-treatment survey-div">
                        <Button
                            ariaLabel={AdminResources.SurveyPositiveButton}
                            subtle={true}
                            tooltipProps={{ text: AdminResources.SurveyPositiveButton }}
                            className="bolt-header-command-item-button survey-icon"
                            iconProps={{ iconName: "Emoji", size: IconSize.medium }}
                            onClick={() => this.onSurveyCardClicked(true)}
                        />
                        <Button
                            ariaLabel={AdminResources.SurveyNegativeButton}
                            subtle={true}
                            tooltipProps={{ text: AdminResources.SurveyNegativeButton }}
                            className="bolt-header-command-item-button survey-icon"
                            iconProps={{ iconName: "Sad", size: IconSize.medium }}
                            onClick={() => this.onSurveyCardClicked(false)}
                        />
                    </div>
                )}
            </div>
        );
    }

    protected onSearchResultCardClicked(scope: SearchScope, routeParameterMapping: { [key: string]: string }, routeId: string) {
        const actionTelemetryLogger = this._startActionCapture("Search Result Clicked", {});
        const url = this.getSearchResultUrl(scope, routeParameterMapping, routeId);
        if (url === undefined) {
            actionTelemetryLogger.captureError("RouteError:" + routeId);
        } else {
            window.open(decodeURIComponent(url), "_blank");
        }
    }

    private getSearchResultUrl(scope: SearchScope, routeParameterMapping: { [key: string]: string }, routeId: string): string | undefined {
        if (SettingsSearchRoutes.find(s => s === routeId) != undefined) {
            if (scope == SearchScope.Project && this.projectSelection.value && this.projectSelection.value.text) {
                routeParameterMapping["project"] = this.projectSelection.value.text;
            }
            var url = routeUrl(this.props.pageContext, routeId, routeParameterMapping);
            return url;
        }
        return undefined;
    }

    private renderRow = (index: number, item: SearchResultSet, details: IListItemDetails<SearchResultSet>, key?: string): JSX.Element => {
        return (
            <ListItem key={key || "list-item" + index} index={index} details={details}>
                <div className="flex-column search-row">
                    <div className="flex search-card-header">
                        <div>
                            <span className="title-xs s">{item.setTitle}</span>
                        </div>
                        {item.seachScope == SearchScope.Project && (
                            <Dropdown
                                className="scope-dropdown"
                                items={this.projectListProvider}
                                onSelect={this.onProjectSelect}
                                selection={this.projectSelection.selection}
                                showFilterBox={true}
                            />
                        )}
                    </div>
                    <div className="search-card-grid">
                        {item.results.map((search, index) => (
                            <div
                                key={item.setTitle + search.title + index}
                                className="search-card flex-column"
                                onClick={() => this.onSearchResultCardClicked(search.scope, search.routeParameterMapping, search.routeId)}
                                tabIndex={0}
                                onKeyDown={ev => this.onKeyDown(ev, search.scope, search.routeParameterMapping, search.routeId)}
                            >
                                <CustomCard className="bolt-card-with-header">
                                    <Header
                                        className="bolt-card-header"
                                        title={search.title}
                                        titleSize={TitleSize.Small}
                                        titleClassName={"text-ellipsis"}
                                        titleIconProps={{ iconName: search.icon }}
                                    />

                                    <CardContent>
                                        <Tooltip overflowOnly={true}>
                                            <div className="text-ellipsis">{search.description}</div>
                                        </Tooltip>
                                    </CardContent>
                                </CustomCard>
                            </div>
                        ))}
                    </div>
                </div>
            </ListItem>
        );
    };

    private onKeyDown = (
        ev: React.KeyboardEvent<HTMLDivElement>,
        scope: SearchScope,
        routeParameterMapping: { [key: string]: string },
        routeId: string
    ) => {
        switch (ev.which) {
            case KeyCode.enter:
                this.onSearchResultCardClicked(scope, routeParameterMapping, routeId);
                break;
            default:
                return;
        }

        ev.preventDefault();
        ev.stopPropagation();
    };

    private onProjectSelect = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>) => {
        this.projectSelection.value = item;
    };

    protected _onQueueToast = (toastMessage: string) => {
        this.messagesService.addToast({
            message: toastMessage,
            duration: 3500,
            forceOverrideExisting: true
        });
    };

    private onTeamSelect = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>) => {};

    private _getTeamsForProject = () => {
        var actionTelemetryLogger = this._startActionCapture("Fetch Teams List", {});
        if (this.projectSelection.value) {
            this.sourceProvider
                .getTeamsForProject(this.projectSelection.value.id)
                .then(
                    teams => {
                        var newTeamsList: IListBoxItem[] = [];
                        teams.forEach(team => {
                            newTeamsList.push({
                                id: team.id,
                                text: team.name
                            });
                        });
                    },
                    reject => {
                        actionTelemetryLogger.captureError(reject.message || String(reject));
                    }
                )
                .catch(reject => {
                    actionTelemetryLogger.captureError(SettingsSearchConstants.GetTeamsListError);
                });
        }
    };
}

VssComponent.register("ms.vss-admin-web.setting-search-page", SettingsSearchComponent);
